import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SurveyDefinition } from '../models/survey-definition/survey-definition';
import { SurveyDefinitionSummary } from '../models/survey-definition/survey-definition-summary';
import { SurveyDefinitionEdit } from '../models/survey-definition/survey-definition-edit';
import { BaseService } from './base.service';
import { SurveyDefinitionClone } from '../models/survey/survey-definition-clone';
import { TeamSurveySummary } from '../models/team/teamSurveySummary';
import { ApiVersions } from './api-version';
import { AttributeDefinition } from '../models/attributes/attributeDefinition';

@Injectable({
  providedIn: 'root'
})
export class SurveyDefinitionService extends BaseService {
  private basePath = 'survey-definitions';
  public surveyDefinitions = new Subject<SurveyDefinitionSummary[]>();
  public sendableSurveyDefinitions = new Subject<SurveyDefinitionSummary[]>();
  public surveyDefinition = new Subject<SurveyDefinition>();
  public surveyDefinitionAttributes = new BehaviorSubject<AttributeDefinition[]>(null);
  private _selectedSurveyDefinition: SurveyDefinition;
  private _isSaving = false;

  public get isSaving() {
    return this._isSaving;
  }
  constructor() {
    super();
  }

  getAll() {
    this.get<SurveyDefinitionSummary[]>(this.basePath)
      .pipe(
        map((data) =>
          data.map((app) => {
            return SurveyDefinitionSummary.parse(app);
          })
        )
      )
      .subscribe((data) => {
        this.surveyDefinitions.next(data);
      });
  }

  getSendable() {
    this.get<SurveyDefinitionSummary[]>(`${this.basePath}/sendable`)
      .pipe(
        map((data) =>
          data.map((app) => {
            return SurveyDefinitionSummary.parse(app);
          })
        )
      )
      .subscribe((data) => {
        this.sendableSurveyDefinitions.next(data);
      });
  }

  getOne(surveyId: string) {
    this.get<SurveyDefinition>(`${this.basePath}/${surveyId}`, false)
      .pipe(
        map((svy) => {
          if (svy) {
            if (!svy.confirmation) {
              svy.confirmation = 'Thank you for completing the survey.';
            }
            return SurveyDefinition.parse(svy);
          }
        })
      )
      .subscribe((data) => {
        this._selectedSurveyDefinition = data;
        this.surveyDefinition.next(this._selectedSurveyDefinition);
        this.surveyDefinitionAttributes.next(
          this._selectedSurveyDefinition.questions.map((x) => x.attributeDefinition)
        );
      });
  }

  update(surveyDef: SurveyDefinitionEdit) {
    this._isSaving = true;
    let path = `${this.basePath}/${this._selectedSurveyDefinition.id}`;
    let body = JSON.stringify(surveyDef);

    return this.put(path, body)
      .pipe(map((response) => this._selectedSurveyDefinition.update(response)))
      .subscribe((data) => {
        this._isSaving = false;
        this.surveyDefinition.next(data);
      });
  }

  create(name: string) {
    let body = JSON.stringify({ title: name, isAnonymous: true });
    return this.post(`${this.basePath}`, body).pipe(map((data) => data?.id));
  }

  clone(fromId: string, clone: SurveyDefinitionClone) {
    const body = JSON.stringify(clone);
    return this.post(`${this.basePath}/${fromId}/clone`, body).pipe(map((data) => data?.id));
  }

  getTeamSurveysSummaryReport(): Observable<TeamSurveySummary[]> {
    return this.get(this.basePath.concat('/team-survey-summary'), true, ApiVersions.v0_1);
  }

  close(id: string) {
    this.put(`${this.basePath}/${id}/close`).subscribe(() => this.getAll());
  }

  open(id: string) {
    this.put(`${this.basePath}/${id}/open`).subscribe(() => this.getAll());
  }

  archiveSurvey(id: string): Observable<any> {
    this._isSaving = true;
    let path = `${this.basePath}/${id}/archive`;
    var obs = this.patch(path).pipe(
      map((data) => {
        if (data) {
          if (this._selectedSurveyDefinition) {
            return this._selectedSurveyDefinition.update(data);
          } else {
            return data;
          }
        }
      })
    );
    obs.subscribe((data) => {
      if (data) {
        this.openSnackBar(`Successfully archived ${data.title}`, true);
        this._isSaving = false;
        this.surveyDefinition.next(data);
      }
    });

    return obs;
  }

  unarchiveSurvey(id: string): Observable<any> {
    this._isSaving = true;
    let path = `${this.basePath}/${id}/unarchive`;
    var obs = this.patch(path).pipe(
      map((data) => {
        if (data) {
          if (this._selectedSurveyDefinition) {
            return this._selectedSurveyDefinition.update(data);
          } else {
            return data;
          }
        }
      })
    );
    obs.subscribe((data) => {
      if (data) {
        this.openSnackBar(`Successfully unarchived ${data.title}`, true);
        this._isSaving = false;
        this.surveyDefinition.next(data);
      }
    });

    return obs;
  }
}
